import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { authApi } from "../services/api";
import { AppUser, LoginRequest } from "../types";

interface AuthContextType {
  user: AppUser | null;
  login: (credentials: LoginRequest) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedUser = localStorage.getItem("authUser");
        const storedToken = localStorage.getItem("authToken");

        if (storedUser && storedToken) {
          // Verify token is still valid
          try {
            await authApi.verify();
            setUser(JSON.parse(storedUser));
          } catch {
            // Token invalid, clear storage
            localStorage.removeItem("authUser");
            localStorage.removeItem("authToken");
          }
        }
      } catch (error) {
        console.error("Error loading auth state:", error);
        localStorage.removeItem("authUser");
        localStorage.removeItem("authToken");
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (
    credentials: LoginRequest,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await authApi.login(credentials);

      if (result.success && result.token && result.user) {
        // Store token and user info
        localStorage.setItem("authToken", result.token);
        localStorage.setItem("authUser", JSON.stringify(result.user));
        setUser(result.user);
        return { success: true };
      }

      // Handle unsuccessful login returned from API
      return { success: false, error: result.message || "Login failed" };
    } catch (error: unknown) {
      console.error("Login error:", error);

      // Extract error message from axios error response
      const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
      const errorFromResponse =
        axiosError.response?.data?.error || axiosError.response?.data?.message;
      const fallbackMessage =
        error instanceof Error ? error.message : "An unexpected error occurred. Please try again.";

      return {
        success: false,
        error: errorFromResponse || fallbackMessage,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
